<script setup>
import { useArtifactStore } from '@/store/artifacts.ts'
import { useInstanceStore } from '@/store/backendInstances'
import { computed, onMounted, ref } from 'vue'
import VideoPayload from '@/components/artifacts/payloads/VideoPayload.vue'
import { useDisplay } from 'vuetify/lib/framework.mjs'
import { imagePayloadHandler } from '@/composables/artifactHelper'

const props = defineProps({
  canvasTimelapse: { type: Object, required: false, default: undefined },
})

const instanceStore = useInstanceStore()
const artifactStore = useArtifactStore()
const { mobile, smAndDown } = useDisplay()

const isLoading = ref(false)
const latestCanvasTimelapseImage = ref(undefined)
const latestCanvasTimelapse = ref(props.canvasTimelapse)

const imagePayload = computed(() => {
  if (!latestCanvasTimelapseImage.value) return null
  return imagePayloadHandler(artifactStore.getArtifactPayload(latestCanvasTimelapseImage.value.artifactId))
})

const timelapseArtifact = computed(() => {
  if (!latestCanvasTimelapse.value) return null
  return artifactStore.getArtifact(latestCanvasTimelapse.value.artifactId)
})

const timelapseIsDownloading = computed(() => {
  if (!latestCanvasTimelapse.value) return false
  return artifactStore.isPayloadDownloading(latestCanvasTimelapse.value.artifactId)
})

const timelapseIsDownloaded = computed(() => {
  if (!latestCanvasTimelapse.value) return false
  return artifactStore.isPayloadDownloaded(latestCanvasTimelapse.value.artifactId)
})

async function downloadTimelapse() {
  if (latestCanvasTimelapse.value) await artifactStore.loadArtifactPayload(latestCanvasTimelapse.value?.artifactId, undefined, null)
}

onMounted(async () => {
  isLoading.value = true

  // Timelapse
  if (!latestCanvasTimelapse.value) {
    latestCanvasTimelapse.value = await artifactStore.getLatestCanvasTimelapse(mobile.value, instanceStore.selectedInstanceId)
  }

  if (!latestCanvasTimelapse.value) return
  // Timelapse image
  const query = {
    query: [
      { key: 'type', operator: '==', value: 'canvas' },
      { key: 'status', operator: 'in', value: ['done', 'archived'] },
      { key: 'timestamp', operator: '>=', value: latestCanvasTimelapse.value.timestamp },
    ],
    order: { timestamp: 'asc' },
  }

  if (mobile.value) {
    query.query.push({ key: 'tags', operator: 'in', value: ['mobile'] })
  } else {
    query.query = [
      [...query.query, { key: 'tags', operator: 'not-contains', value: 'mobile' }],
      [...query.query, { key: 'tags', operator: 'exists', value: false }],
    ]
  }

  latestCanvasTimelapseImage.value = await artifactStore.queryOneArtifact(query, instanceStore.selectedInstanceId)

  if (latestCanvasTimelapseImage.value) {
    await artifactStore.loadArtifactPayload(latestCanvasTimelapseImage.value.artifactId, undefined, null) // instantly download the timelapse image
  }
  isLoading.value = false
})
</script>
<template>
  <v-skeleton-loader
    v-if="isLoading"
    width="100%"
    class="rounded-lg"
    type="card"
  />
  <div v-else>
    <v-img
      v-if="!timelapseIsDownloaded"
      :src="imagePayload"
      min-height="100"
      class="cursor-pointer rounded-lg"
      @click="downloadTimelapse"
    >
      <div
        class="d-flex h-100 flex-row justify-center align-center"
        style="background-color: rgba(0, 0, 0, 0.5)"
      >
        <v-icon
          v-if="!timelapseIsDownloading"
          icon="mdi-play"
          :size="smAndDown ? 32 : 64"
          color="white"
        />
        <v-progress-circular
          v-if="timelapseIsDownloading"
          indeterminate
          :size="smAndDown ? 32 : 64"
        />
      </div>
    </v-img>

    <VideoPayload
      v-if="timelapseIsDownloaded"
      :artifact-id="latestCanvasTimelapse.artifactId"
      :payload="artifactStore.getArtifactPayload(latestCanvasTimelapse.artifactId)"
      :is-downloaded="true"
      :artifact-data="timelapseArtifact"
    />
  </div>
</template>
